// utils/constants.js - Constantes y configuraciones centralizadas con branding corporativo

// ─── Fuente única de verdad para técnicos ─────────────────────────────────────
export const TECNICOS_EMPLEADOS = [
  { id: 21936, nombre: 'Benito' },
  { id: 23415, nombre: 'Pablo' },
  { id: 21945, nombre: 'Dani' },
  { id: 31171, nombre: 'Ivan' },
];

export const getTecnicoNombre = (id) =>
  TECNICOS_EMPLEADOS.find(t => t.id === Number(id))?.nombre || null;

// ─── Sistema de puntos de rutas ────────────────────────────────────────────────
export const PUNTOS_CIERRE = { BASICO: 5, RACHA: 8, COMPLETO: 10, COMPLETO_RACHA: 12 };

export const NIVELES_PUNTOS = [
  { min: 0,    nombre: 'Novato',       emoji: '🌱', color: '#9E9E9E' },
  { min: 60,   nombre: 'Aprendiz',     emoji: '⭐', color: '#2196F3' },
  { min: 180,  nombre: 'Competente',   emoji: '💪', color: '#4CAF50' },
  { min: 360,  nombre: 'Experto',      emoji: '🔥', color: '#FF9800' },
  { min: 600,  nombre: 'Maestro',      emoji: '🏆', color: '#F44336' },
  { min: 960,  nombre: 'Leyenda',      emoji: '👑', color: '#9C27B0' },
  { min: 1440, nombre: 'Gran Maestro', emoji: '💎', color: '#00BCD4' },
];

export const getLevelFromPoints = (pts) =>
  [...NIVELES_PUNTOS].reverse().find(n => pts >= n.min) || NIVELES_PUNTOS[0];

export const tecnicoColors = {
  Benito: "#3B82F6", // Azul
  Pablo: "#10B981",  // Verde
  Dani: "#F59E0B",   // Naranja
  Ivan: "#EF4444",   // Rojo
  Cliente: "#8B5CF6" // Púrpura para cliente
};

export const calendario = {
  Lunes: { Benito: ["Zona 5"], Ivan: ["Zona 4"], Pablo: ["Zona 1-2"], Dani: ["Zona 3"] },
  Martes: { Benito: ["Zona 6"], Ivan: ["Zona 4"], Pablo: ["Zona 1-2"], Dani: ["Zona 3"] },
  Miercoles: { Benito: ["Zona 5"], Ivan: ["Zona 4"], Pablo: ["Zona 1-2"], Dani: ["Zona 3"] },
  Jueves: { Benito: ["Zona 5"], Ivan: ["Zona 4"], Pablo: ["Zona 1-2"], Dani: ["Zona 3"] },
  Viernes: { Benito: ["Zona 6"], Ivan: ["Zona 4"], Pablo: ["Zona 1-2"], Dani: ["Zona 3"] }
};

// ─── Roster por defecto (fallback de la colección Firestore `tecnicos`) ───────
// Forma canónica del roster. Los consumidores nuevos leen de Firestore vía
// `tecnicosService` / `useTecnicos`; esto solo se usa mientras la colección
// esté vacía o no disponible. Deriva de TECNICOS_EMPLEADOS + tecnicoColors +
// calendario para no duplicar la fuente durante la migración.
export const TECNICOS_FALLBACK = TECNICOS_EMPLEADOS.map((t, i) => ({
  id:         String(t.id),
  employeeId: t.id,
  nombre:     t.nombre,
  color:      tecnicoColors[t.nombre] || '#6B7280',
  zonas:      Object.keys(calendario).reduce((acc, dia) => {
    acc[dia] = calendario[dia][t.nombre] || [];
    return acc;
  }, {}),
  activo:     true,
  orden:      i,
}));

// Roles disponibles en el sistema (excluye 'tecnico' que tiene su propia app)
// Usar estas constantes en la propiedad `roles` de cada item del menú.
export const ROLES = {
  ADMIN:        'admin',
  COORDINADOR:  'coordinador_operaciones',
  ATENCION:     'atencion_cliente',
};

// Accesos directos a grupos de roles de uso frecuente
const TODOS     = [ROLES.ADMIN, ROLES.COORDINADOR, ROLES.ATENCION];
const OPS       = [ROLES.ADMIN, ROLES.COORDINADOR];
const SOLO_ADMIN = [ROLES.ADMIN];

export const menuItems = [
  // ── DIRECCIÓN Y OPERACIONES ────────────────────────────────────────────────
  {
    id: 'dashboard',
    icon: 'Layout',
    label: 'Dashboard',
    description: 'Resumen y alertas del día',
    roles: TODOS,
  },

  // ── COMUNICACIONES (todos los roles de oficina) ────────────────────────────
  {
    id: 'comunicaciones',
    icon: 'MessageCircle',
    label: 'Comunicaciones',
    description: 'CRM, email y atención al cliente',
    roles: TODOS,
    subitems: [
      {
        id: 'incidence',
        label: 'CRM',
        description: 'Bandeja, nuevo contacto, clientes y analítica',
        icon: 'Users',
        roles: TODOS,
      },
      {
        id: 'crm-email',
        label: 'CRM Email',
        description: 'Bandeja de entrada compartida',
        icon: 'Mail',
        roles: TODOS,
      },
      {
        id: 'leads',
        label: 'Leads Ads',
        description: 'Leads de Facebook Ads',
        icon: 'Zap',
        roles: SOLO_ADMIN,
      },
    ],
  },

  // ── PLANIFICADOR DE CITAS (atención + operaciones) ─────────────────────────
  {
    id: 'planner',
    icon: 'Calendar',
    label: 'Planificador de Citas',
    description: 'Disponibilidad y citas por CP',
    roles: TODOS,
  },

  // Agenda Semanal se movió dentro de "Órdenes de trabajo" (Z v2.0) como
  // otra pestaña más, junto a "En campo hoy"/"Todas" — tenía sentido con
  // esa parte, no como entrada de menú aparte. 2026-09-16.

  // ── CALENDARIO INTERNO (reuniones y tareas del equipo de oficina) ──────────
  {
    id: 'calendario',
    icon: 'Calendar',
    label: 'Calendario',
    description: 'Reuniones, tareas y huecos del equipo',
    roles: TODOS,
  },

  // ── OPERACIONES (solo admin y coordinador) ─────────────────────────────────
  {
    id: 'technicians',
    icon: 'Users',
    label: 'Operaciones',
    description: 'Zonas y técnicos',
    roles: OPS,
    subitems: [
      {
        id: 'tecnicos-admin',
        label: 'Técnicos',
        description: 'Roster, colores y zonas por día',
        icon: 'Users',
        roles: OPS,
      },
      {
        id: 'zones',
        label: 'Mapa de Zonas',
        description: 'Distribución territorial',
        icon: 'MapPin',
        roles: OPS,
      },
      // Rutas y Comisiones se movieron dentro de "Órdenes de trabajo" (Z
      // v2.0) como pestañas más (mismo criterio que Agenda Semanal) — ya no
      // son entradas de menú aparte. 2026-09-16.
    ],
  },

  // ── KANBAN COMERCIAL ────────────────────────────────────────────────────────
  // El Kanban Operativo (órdenes pendientes por estado) se retiró — ese mismo
  // agrupado por estado ya vive dentro de "Órdenes de trabajo" → Todas →
  // vista Kanban, sobre los mismos datos. 2026-09-17.
  {
    id: 'kanbanComercial',
    icon: 'DollarSign',
    label: 'Kanban Comercial',
    description: 'Presupuestos y pipeline comercial',
    roles: SOLO_ADMIN,
  },

  // ── LIQUIDACIONES (solo admin — implica facturación y cobros) ──────────────
  {
    id: 'liquidaciones',
    icon: 'DollarSign',
    label: 'Liquidaciones',
    description: 'Garantías y pre-liquidaciones por marca',
    roles: SOLO_ADMIN,
  },

  // ── MATERIAL ───────────────────────────────────────────────────────────────
  {
    id: 'material',
    icon: 'Package',
    label: 'Gestión de Material',
    description: 'Pedidos y Excel para Ariston',
    roles: OPS,
  },

  // ── FLOTA ──────────────────────────────────────────────────────────────────
  {
    id: 'flota',
    icon: 'Truck',
    label: 'Flota',
    description: 'Gestión de furgonetas y vehículos',
    roles: OPS,
  },

  // ── MANTENIMIENTOS ─────────────────────────────────────────────────────────
  {
    id: 'mantenimientos',
    icon: 'Wrench',
    label: 'Mantenimientos',
    description: 'Contratos anuales NUOS Ariston',
    roles: TODOS,
  },

  // ── FICHAJES ───────────────────────────────────────────────────────────────
  {
    id: 'fichajes',
    icon: 'Clock',
    label: 'Fichajes',
    description: 'Control horario — RDL 8/2019',
    roles: OPS,
  },

  // ── HERRAMIENTAS ──────────────────────────────────────────────────────────
  {
    id: 'herramientas',
    icon: 'Wrench',
    label: 'Herramientas',
    description: 'Utilidades y configuración',
    roles: TODOS,
    subitems: [
      {
        id: 'pegatinas',
        label: 'Pegatinas',
        description: 'Genera pegatinas para órdenes',
        icon: 'Tag',
        roles: OPS,
      },
      {
        id: 'keystore',
        label: 'Keystore',
        description: 'Contraseñas y credenciales',
        icon: 'Lock',
        roles: SOLO_ADMIN,
      },
      {
        id: 'guia',
        label: 'Guía de usuario',
        description: 'Manual de uso de la aplicación',
        icon: 'BookOpen',
        roles: TODOS,
      },
      {
        id: 'config-empresa',
        label: 'Configuración empresa',
        description: 'Nombre, NIF, dirección e IBAN para facturas',
        icon: 'FileText',
        roles: SOLO_ADMIN,
      },
    ],
  },

  // ── SOLO ADMIN ─────────────────────────────────────────────────────────────
  {
    id: 'users',
    icon: 'Users',
    label: 'Gestión de Usuarios',
    description: 'Crear y administrar accesos',
    roles: SOLO_ADMIN,
  },
  {
    id: 'scripts-admin',
    icon: 'FileText',
    label: 'Scripts CRM',
    description: 'Configurar guías de atención al cliente',
    roles: SOLO_ADMIN,
  },
  {
    id: 'menu-admin',
    icon: 'Lock',
    label: 'Editor de menú',
    description: 'Visibilidad y agrupación por rol',
    roles: SOLO_ADMIN,
  },
  {
    id: 'errores-admin',
    icon: 'FileText',
    label: 'Errores de la app',
    description: 'Fallos capturados en producción',
    roles: SOLO_ADMIN,
  },
  {
    id: 'emails-admin',
    icon: 'Mail',
    label: 'Correos a clientes',
    description: 'Registro de envíos: entregados, rechazados y con error',
    roles: SOLO_ADMIN,
  },
  // Visible para OPS (no solo admin): es dirección/coordinación quien decide
  // si se da de alta una pieza en StelOrder, no una tarea exclusiva de admin.
  {
    id: 'piezas-pendientes-admin',
    icon: 'Package',
    label: 'Piezas pendientes de alta',
    description: 'Referencias escritas/escaneadas por técnicos sin coincidencia en el catálogo',
    roles: OPS,
  },
  {
    id: 'catalogo-piezas-admin',
    icon: 'Package',
    label: 'Catálogo de piezas por modelo',
    description: 'Piezas sugeridas por equipo/modelo a partir del uso real — editar nombre, verificar o borrar',
    roles: OPS,
  },
  {
    id: 'v2',
    icon: 'Zap',
    label: 'Z v2.0',
    description: 'Nuevo ERP — vista previa admin',
    roles: SOLO_ADMIN,
    subitems: [
      { id: 'v2-clientes',     label: 'Clientes',              description: 'Gestión completa de clientes',                      icon: 'Users',      roles: TODOS },
      { id: 'v2-ordenes',      label: 'Órdenes de trabajo',    description: 'Kanban y lista de órdenes',                          icon: 'Calendar',   roles: OPS },
      { id: 'v2-seed',         label: 'Cargar datos de prueba',description: 'Seed con clientes y órdenes de ejemplo',             icon: 'Package',    roles: SOLO_ADMIN },
      { id: 'v2-almacen',      label: 'Almacén',               description: 'Stock, productos y catálogo',                        icon: 'Package',    roles: SOLO_ADMIN },
      { id: 'v2-facturas',     label: 'Facturas',              description: 'Facturas, bancos y analítica financiera',            icon: 'DollarSign', roles: SOLO_ADMIN },
      { id: 'v2-informes',     label: 'Informes',              description: 'Analítica, KPIs y exportación',                      icon: 'BarChart2',  roles: SOLO_ADMIN },
      { id: 'v2-migracion',    label: 'Migración Firebase',    description: 'Importar StelOrder → Firebase y activar switch',     icon: 'Refresh',    roles: SOLO_ADMIN },
      { id: 'v2-proveedores',  label: 'Proveedores',           description: 'Gestión de proveedores y alertas de stock',          icon: 'Package',    roles: SOLO_ADMIN },
      { id: 'v2-presupuestos', label: 'Presupuestos',          description: 'Presupuestos digitales con líneas de material',      icon: 'DollarSign', roles: SOLO_ADMIN },
      { id: 'v2-instalaciones',label: 'Instalaciones',         description: 'Proyectos de instalación con seguimiento por fases', icon: 'Wrench',     roles: SOLO_ADMIN },
      { id: 'v2-bancos',       label: 'Bancos',                description: 'Ahora es una pestaña de Facturas',                   icon: 'DollarSign', roles: SOLO_ADMIN },
      { id: 'v2-conductos',    label: 'Calculadora de conductos', description: 'Diseño y plano de corte de instalaciones de conductos', icon: 'Wrench',  roles: SOLO_ADMIN },
    ],
  },
];

// Función para generar datos simulados de carga de trabajo
export const generateWorkloadData = () => {
  const workloadData = {};
  const days = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes'];
  const technicians = ['Benito', 'Pablo', 'Dani', 'Ivan'];
  
  days.forEach(day => {
    workloadData[day] = {};
    technicians.forEach(tech => {
      workloadData[day][tech] = Math.floor(Math.random() * 7); // 0-6 órdenes
    });
  });
  
  return workloadData;
};

// Paleta de colores corporativa adicional para uso interno
export const brandColors = {
  primary: {
    main: '#DC2626',      // Rojo principal
    dark: '#991B1B',      // Rojo oscuro
    light: '#FEE2E2',     // Rojo muy claro
    hover: '#B91C1C'      // Rojo hover
  },
  secondary: {
    main: '#374151',      // Gris oscuro
    dark: '#1F2937',      // Gris muy oscuro/negro
    light: '#F9FAFB',     // Gris muy claro
    medium: '#6B7280'     // Gris medio
  },
  neutral: {
    white: '#FFFFFF',
    black: '#000000',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    }
  }
};